import { openDB, IDBPDatabase } from 'idb';
import type { PhotoAnnotation } from '../types/photo.types';
import type { SignificationSubmission } from '../types/signification.types';

const DB_NAME = 'dekko_offline_db';
const DB_VERSION = 3; // v3: added pending_uploads store
const MEDIA_STORE = 'media_blobs';
const PENDING_UPLOADS_STORE = 'pending_uploads';

let dbPromise: Promise<IDBPDatabase> | null = null;

const getDB = () => {
    if (!dbPromise) {
        dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db, oldVersion) {
                // Media blobs (added in v2)
                if (!db.objectStoreNames.contains(MEDIA_STORE)) {
                    db.createObjectStore(MEDIA_STORE, { autoIncrement: true });
                }
                // Pending uploads — persists blobs before upload so they survive navigation (v3)
                if (oldVersion < 3 && !db.objectStoreNames.contains(PENDING_UPLOADS_STORE)) {
                    db.createObjectStore(PENDING_UPLOADS_STORE, { keyPath: 'id', autoIncrement: true });
                }
                // NOTE: the v1 'submissions_queue' store is deliberately no longer
                // created. It was a write-only dead end — nothing ever drained it —
                // and Firestore's persistentLocalCache now queues offline writes
                // durably. Existing databases keep the orphaned store; it is unused.
            },
        });
    }
    return dbPromise;
};

/**
 * Saves a media blob to IndexedDB. Returns the auto-generated key.
 */
export const saveMediaBlob = async (blob: Blob): Promise<number> => {
    const db = await getDB();
    const key = await db.add(MEDIA_STORE, blob);
    return key as number;
};

/**
 * Retrieves a media blob from IndexedDB by key.
 */
export const getMediaBlob = async (key: number): Promise<Blob | undefined> => {
    const db = await getDB();
    return db.get(MEDIA_STORE, key);
};

/**
 * Removes a media blob from IndexedDB.
 */
export const removeMediaBlob = async (key: number): Promise<void> => {
    const db = await getDB();
    await db.delete(MEDIA_STORE, key);
};

// ─── Pending Uploads (persist blobs before upload) ─────────────

export interface PendingUpload {
    id?: number;
    submissionId: string;
    projectId: string;
    participantId: string;
    taskId: string;
    taskTitle?: string;
    mediaType: string;
    notes: string;
    blobKeys: number[];
    createdAt: number;
    parentSubmissionId?: string | null;
    submissionType?: 'original' | 'redo' | 'addendum';
    attemptNumber?: number;
    /** Photo annotations (pins incl. voice-note Blobs — structured clone
        stores Blobs fine). Persisted so recover() after a reload doesn't
        silently drop voice pins from the resurrected upload. */
    photoAnnotations?: PhotoAnnotation[];
    /** Signification responses (the pole/narrative JSON). Persisted BEFORE any
        network work so a failed submit doesn't take the answers with it — the
        blobs alone are useless without them. */
    significationData?: SignificationSubmission;
}

/**
 * Persist blobs to IndexedDB and record a pending upload.
 * Call this BEFORE starting the network upload so blobs survive navigation.
 */
export const savePendingUpload = async (
    meta: Omit<PendingUpload, 'id' | 'blobKeys' | 'createdAt'>,
    blobs: Blob[],
): Promise<number> => {
    const blobKeys = await Promise.all(blobs.map(b => saveMediaBlob(b)));
    const db = await getDB();
    const id = await db.add(PENDING_UPLOADS_STORE, {
        ...meta,
        blobKeys,
        createdAt: Date.now(),
    });
    return id as number;
};

/** Get all pending uploads. */
export const getPendingUploads = async (): Promise<PendingUpload[]> => {
    const db = await getDB();
    return db.getAll(PENDING_UPLOADS_STORE);
};

/** Remove a pending upload and its blobs after successful submission. */
export const removePendingUpload = async (id: number): Promise<void> => {
    const db = await getDB();
    const record = await db.get(PENDING_UPLOADS_STORE, id) as PendingUpload | undefined;
    if (record?.blobKeys) {
        await Promise.all(record.blobKeys.map(k => removeMediaBlob(k).catch(() => {})));
    }
    await db.delete(PENDING_UPLOADS_STORE, id);
};

/**
 * Clean up pending uploads older than maxAgeMs.
 *
 * `keepIds` exempts records that are live in the UploadManager's queue. Without
 * it this function is a data-loss bug: it used to run at module load, so a
 * record older than maxAgeMs was deleted before recover() could re-enqueue it,
 * destroying the participant's only copy of the captured media. Callers must
 * pass the ids they are about to retry.
 */
export const clearStalePendingUploads = async (
    maxAgeMs: number = 24 * 60 * 60 * 1000,
    keepIds?: Set<number>,
): Promise<void> => {
    const all = await getPendingUploads();
    const cutoff = Date.now() - maxAgeMs;
    for (const pu of all) {
        if (pu.createdAt < cutoff && pu.id && !keepIds?.has(pu.id)) {
            await removePendingUpload(pu.id);
        }
    }
};
